import React from 'react';
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';

import { AttrsHelper } from '../../../sb-helpers/attrs-helper';

import { RawTextareaEditablePlugin } from './RawTextareaEditablePlugin';
import { RawTextareaPastePlugin } from './RawTextareaPastePlugin';
import { RawTextareaEnterPlugin } from './RawTextareaEnterPlugin';
import { RawTextareaRichTextPlugin } from './RawTextareaRichTextPlugin';
import { RawTextareaToolbar } from './RawTextareaToolbar';
import {
  EDITOR_CLASS_NAME,
  RAW_TEXTAREA_EDITOR_THEME,
  SAPLING_COMPAT_CLASS_NAME,
} from './textarea-editor-theme.const';
import { TextareaValueFormatters } from './textarea-value-formatters';
import {
  $importTextareaMarkdown,
  RawTextareaValuePlugin,
} from './RawTextareaValuePlugin';
import type { TextareaFormattingType } from './textarea-markdown-util';
import { TextareaMarkdownUtil } from './textarea-markdown-util';
import { TextareaPasteConversion } from './textarea-paste-conversion';

export type RawTextareaValueType = string | null;

export type RawTextareaProps = {
  className?: string;
  disabled?: boolean;
  /**
   * Which newline convention the stored value uses. Defaults to `legacy`, the
   * convention every field was written in before this prop existed. Pass
   * `modern` only for a value that has never been through the legacy editor.
   */
  formatting?: TextareaFormattingType;
  full?: boolean;
  id?: string;
  invalid?: boolean;
  name: string;
  /**
   * The editable surface is a contenteditable `div`, so these receive a focus
   * event for a div rather than an input-change event. No caller reads the
   * argument.
   */
  onBlur?: (e: React.FocusEvent<HTMLDivElement>) => void;
  onChange?: (value: RawTextareaValueType) => Promise<unknown> | void;
  /**
   * Called instead of inserting a paragraph when Enter is pressed on its own,
   * which makes Shift+Enter the way to add a line. Left off, Enter inserts a
   * paragraph as it always has - see `RawTextareaEnterPlugin`.
   */
  onEnter?: () => void;
  onFocus?: (e: React.FocusEvent<HTMLDivElement>) => void;
  placeholder?: string;
  readonly?: boolean;
  stretched?: boolean;
  value?: RawTextareaValueType;
  /**
   * `seamless` drops the field's own box - border, radius, padding, background
   * and height bounds - so it reads as part of the surface it sits in, such as a
   * composer inside a `Card`. Without a height cap the field grows with its
   * content, which leaves scrolling to that surface, and the error state has no
   * border left to color.
   */
  variation?: 'default' | 'seamless';
};

export const RawTextarea = ({
  className = '',
  disabled = false,
  formatting = 'legacy',
  full = false,
  id,
  invalid = false,
  name,
  onBlur,
  onChange,
  onEnter,
  onFocus,
  placeholder,
  readonly,
  stretched,
  value = '',
  variation = 'default',
}: RawTextareaProps) => {
  const isEditable = !disabled && !readonly;
  const normalizedValue = value ?? '';

  return (
    <div
      className={AttrsHelper.formatClassname(
        'raw-textarea',
        invalid && 'error-state',
        disabled && 'disabled-state',
        readonly && 'readonly-state',
        stretched && 'stretched-state',
        full && 'full-mode',
        variation === 'seamless' && 'raw-textarea-seamless',
        className
      )}
      aria-placeholder={placeholder}
      data-test={name}
      id={id}
    >
      <LexicalComposer
        initialConfig={{
          // Read once, at mount. RawTextareaEditablePlugin keeps it in step
          // afterwards.
          editable: isEditable,
          editorState: () => {
            $importTextareaMarkdown({
              markdown: normalizedValue,
              formatting,
            });
          },
          html: { import: TextareaPasteConversion.htmlImport },
          /**
           * The field name, so each textarea gets its own namespace. Lexical
           * stamps this on clipboard data and refuses its own rich payload when
           * the namespace differs, meaning a copy between two textareas pastes
           * as markdown text rather than editor state.
           */
          namespace: name,
          nodes: TextareaMarkdownUtil.nodes,
          /**
           * Rethrown rather than swallowed: an error here means the editor state
           * and the DOM have diverged, and silently continuing risks persisting
           * a corrupted value. Better to surface it to the app's error boundary.
           */
          onError: (error: Error) => {
            throw error;
          },
          theme: RAW_TEXTAREA_EDITOR_THEME,
        }}
      >
        {full && isEditable ? <RawTextareaToolbar name={name} /> : null}

        {/*
          Not handed to ContentEditable: Lexical's own placeholder settles its
          visibility state after mount, which React reports as an update outside
          act(). Deriving it from the controlled value avoids that.
        */}
        {placeholder && !normalizedValue ? (
          <div className="raw-textarea-placeholder">{placeholder}</div>
        ) : null}

        <RawTextareaRichTextPlugin />
        <RawTextareaPastePlugin />
        <RawTextareaEditablePlugin isEditable={isEditable} />
        <RawTextareaEnterPlugin onEnter={onEnter} />
        <ContentEditable
          className={`${EDITOR_CLASS_NAME} ${SAPLING_COMPAT_CLASS_NAME}`}
          onBlur={onBlur}
          onFocus={onFocus}
        />

        <RawTextareaValuePlugin
          formatting={formatting}
          formatValue={TextareaValueFormatters[formatting]}
          onChange={onChange}
          value={normalizedValue}
        />
        <HistoryPlugin />
        <ListPlugin />
        <CheckListPlugin />
        <LinkPlugin />
        <TablePlugin />
        <TabIndentationPlugin />
        <MarkdownShortcutPlugin
          transformers={TextareaMarkdownUtil.transformers}
        />
      </LexicalComposer>
    </div>
  );
};
